import { useState } from 'react';
import CollapsedCard from './CollpasedCard';
import Amount, { IconText } from './Amount';
import { SortedMilitaryHand } from './PlayerArea';
import { EntryLines } from './Journal';
import CardBack from './CardBack';
const { Modal } = require('react-bootstrap');

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// A row of the end-of-turn block: its label, then what it is
const Row = ({label, children}) => <>
    <div className="tta-end-turn-label">{label}</div>
    <div className="tta-end-turn-value">{children}</div>
</>;

// Shown every time the turn is ended: what the end of the turn will do, warnings (uprising, corruption, famine,
// unspent civil actions), and the military cards to discard when over the hand limit. "Keep playing" goes back.
// With Saladin, his choice for until the next one is made here too: the turn cannot end until it is (the current one
// is marked). Beside it all, a summary of the turn: its journal entries (since the last turn ended), then the moves
// not yet sent.
function EndTurnDialog({Preview, MilitaryHand, GameViewModel, OnConfirm, OnCancel})
{
    const journal = GameViewModel.journal;
    const lastEnd = journal.map(entry => entry.turnEnd).lastIndexOf(true);
    const thisTurn = [...journal.slice(lastEnd + 1).map(entry => entry.description),
        ...GameViewModel.pendingMoves.map(entry => entry.description)];
    const [selected, setSelected] = useState([]);
    const toggle = (id) => setSelected(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
    const [saladin, setSaladin] = useState(null);
    const discardReady = selected.length === Preview.discard;
    const ready = discardReady && (!Preview.saladin || saladin !== null);

    // Problems in red; unspent civil actions as a softer reminder, since leaving them can be intentional
    const warnings = [];
    if (Preview.uprising)
    {
        warnings.push(<>Uprising: {plural(Preview.discontent, 'discontent worker')} but only {plural(Preview.unusedWorkers, 'unused worker')}.
            Your production phase will be skipped (no culture, science, food or resources).</>);
    }
    if (!Preview.uprising && Preview.corruption > 0)
    {
        warnings.push(<>Corruption: you will lose <Amount value={Preview.corruption} kind="resources"/>
            {Preview.corruptionFromFood > 0 && <> (<Amount value={Preview.corruptionFromFood} kind="food"/> of it as food, since you are short of resources)</>}.</>);
    }
    if (!Preview.uprising && Preview.famine > 0)
    {
        warnings.push(<>Famine: you will be <Amount value={Preview.famine} kind="food"/> short and lose <Amount value={Preview.famine * 4} kind="culture"/>.</>);
    }

    return (
        <Modal show onHide={OnCancel} centered dialogClassName="tta-end-turn-dialog">
            <Modal.Header closeButton style={{ padding: '8px 12px' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>End your turn?</Modal.Title>
            </Modal.Header>
            <Modal.Body className="tta tta-left tta-end-turn-body">
                <div className="tta-end-turn-summary">
                    <div className="tta-since-title">This turn</div>
                    {thisTurn.length === 0 ? <div className="tta-muted">Nothing done yet</div> :
                        thisTurn.map((text, index) => <div key={index} className="tta-end-turn-entry"><EntryLines text={text}/></div>)}
                </div>
                <div className="tta-end-turn-main">
                    {warnings.map((warning, index) => <div key={index} className="tta-warning">{warning}</div>)}
                    {Preview.civilActionsLeft > 0 &&
                        <div className="tta-reminder">
                            You have <Amount value={Preview.civilActionsLeft} kind="CA"/> unspent ({plural(Preview.civilActionsLeft, 'civil action')}).
                        </div>}

                    {/* What the end of the turn will do, a row each under a label: one-time scoring separate from production
                        (e.g. Genghis Khan: "Name: what", the name as the label), corruption (as in the warnings above; it comes
                        first), production (the food the workers eat is in it), and the military cards drawn, face down */}
                    <div className="tta-end-turn-block">
                        <div className="tta-since-title">At the end of your turn</div>
                        <div className="tta-end-turn-grid">
                            {Preview.endOfTurn?.map((line, index) => {
                                const at = line.indexOf(': ');
                                return at < 0
                                    ? <div key={index} className="tta-end-turn-wide"><IconText text={line}/></div>
                                    : <Row key={index} label={line.slice(0, at)}><IconText text={line.slice(at + 2)}/></Row>;
                            })}
                            {Preview.endOfTurnNotes?.map((line, index) =>
                                <div key={`note ${index}`} className="tta-end-turn-wide tta-muted"><IconText text={line}/></div>)}
                            {!Preview.uprising && Preview.produced && Preview.corruption > 0 &&
                                <Row label="Corruption">
                                    <Amount value={-(Preview.corruption - Preview.corruptionFromFood)} kind="resources"/>
                                    {Preview.corruptionFromFood > 0 && <Amount value={-Preview.corruptionFromFood} kind="food"/>}
                                </Row>}
                            {Preview.uprising
                                ? <Row label="Production"><span className="tta-muted">None (uprising)</span></Row>
                                : <Row label="Production">
                                    <Amount value={Preview.produced?.culture ?? Preview.culture} kind="culture" signed/>
                                    <Amount value={Preview.produced?.science ?? Preview.science} kind="science" signed/>
                                    <Amount value={Preview.produced?.food ?? Preview.food} kind="food" signed/>
                                    <Amount value={Preview.produced?.resources ?? Preview.resources} kind="resources" signed/>
                                </Row>}
                            <Row label="Military cards">
                                {Preview.militaryDraw === 0 ? <span className="tta-muted">None drawn</span> :
                                    <div className="tta-card-backs">
                                        {Array.from({ length: Preview.militaryDraw }, (_, index) => <CardBack key={index} age={GameViewModel.age}/>)}
                                    </div>}
                            </Row>
                        </div>
                    </div>

                    {Preview.saladin &&
                        <div className="tta-end-turn-line">
                            <span className="tta-since-title">Saladin</span>
                            {[['strength', <Amount value={2} kind="strength" signed/>], ['civil', <Amount value={1} kind="CA" signed/>]].map(([value, amount]) =>
                                <button key={value} className={`tta-button${saladin === value ? ' dark' : ''}`} onClick={() => setSaladin(value)}
                                    title={value === 'strength' ? '+2 strength until your next choice' : '+1 civil action until your next choice'}>{amount}
                                    {Preview.saladin.current === value && <span className="tta-cost-note"> (now)</span>}</button>)}
                            {saladin === null && <span className="tta-muted">Choose one to end your turn</span>}
                        </div>}

                    {Preview.discard > 0 &&
                        <div>
                            <div className="tta-since-title">Discard {plural(Preview.discard, 'military card')}</div>
                            <div className="tta-muted" style={{ marginBottom: '6px' }}>
                                You may keep as many military cards as your military actions.
                            </div>
                            {SortedMilitaryHand(MilitaryHand).map(card =>
                                <label key={card.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selected.includes(card.id)} onChange={() => toggle(card.id)}/>
                                    <div style={{ flex: 1 }}><CollapsedCard card={card}/></div>
                                </label>)}
                        </div>}
                </div>
            </Modal.Body>
            <Modal.Footer className="tta" style={{ padding: '6px 12px' }}>
                {Preview.discard > 0 &&
                    <span className="tta-muted" style={{ marginRight: 'auto' }}>{selected.length} of {Preview.discard} chosen</span>}
                <button className="tta-button" onClick={OnCancel}>Keep playing</button>
                <button className="tta-button dark" disabled={!ready} onClick={() => OnConfirm(selected, Preview.saladin ? saladin : undefined)}>
                    {Preview.discard > 0 ? 'Discard and end turn' : 'End turn'}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default EndTurnDialog;